import React, { FC, memo, useMemo } from 'react';
import { ChatMessage } from '../../../../../common/store/chat/types/chat-message';
import { UserChatMessageContainer } from './user-chat-message-container';
import { UserChatMessageCloud } from './user-chat-message-cloud';
import { BotChatMessageAvatar } from '../bot-chat-message/bot-chat-message-avatar';
import styled, { useTheme } from 'styled-components';
import { Plug } from '../../../../components/plug';
import { parseUserMessage } from '../../../../utils/parse-message';
import { PiiNote } from './pii-note';
import { UserChatMessageTimestamp } from './user-chat-message-timestamp';

interface UserChatMessageProps {
  message: ChatMessage;
  isLastMessage: boolean;
}

export const UserChatMessage: FC<UserChatMessageProps> = memo(({
  message,
  isLastMessage,
}) => {
  const theme = useTheme();
  const {isHavePII, text} = useMemo(
    () => ({
      text: parseUserMessage(message.value),
      isHavePII: message.value.includes('[PII]'),
    }),
    [message.value],
  );

  const formattedTime = useMemo(() => {
    if (!message.timestamp) return '';
  
    const timestampInSeconds = Number(message.timestamp);
  
    if (!isNaN(timestampInSeconds)) {
      const date = new Date(timestampInSeconds * 1000); // Convert seconds → ms
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
  
    return '';
  }, [message.timestamp]);

  type MessageStatus = "sending" | "sent" | "delivered" | "seen" | null;
  const messageStatus: MessageStatus = useMemo(() => {
    if (message.message_status === 'sending') return 'sending';
    if (message.message_status === 'sent') return 'sent';
    if (message.message_status === 'delivered') return 'delivered';
    if (message.message_status === 'seen') return 'seen';
    return null;
  }, [message.message_status]);
  

  return (
    <>
      <UserChatMessageContainer id={isLastMessage ? 'user-last-message' : undefined} className="voa_chat_user_message">
        <CustomPlug>
          <BotChatMessageAvatar
            src={theme?.themeConfig?.chatMessages?.botMessage?.avatar?.src}
          />
        </CustomPlug>

        <UserChatMessageCloud
          dangerouslySetInnerHTML={{ __html: text }}
          className="voa_chat_user_message_text"
        />

      </UserChatMessageContainer>
      {formattedTime && (
        <UserChatMessageTimestamp
          timestamp={formattedTime}
          actor={message.itemType}
          messageStatus={messageStatus}
        />
      )}
      {isHavePII && (
        <PiiNote>
          PII masked for your privacy
        </PiiNote>
      )}
    </>
  );
});


export const CustomPlug = styled(Plug)`
  height: 0;
`